import { Component, computed, effect, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalDialogComponent } from 'src/app/controls/components/advanced/modal-dialog/modal-dialog.component';
import { ButtonComponent } from 'src/app/controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from 'src/app/controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from 'src/app/controls/directives/button-secondary.directive';
import { DocumentDirection, TransportType } from '../../models/partner-onboarding';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { RadiobuttonsComponent } from 'src/app/controls/components/basic/radiobuttons/radiobuttons.component';

@Component({
  selector: 'app-select-transport-dialog',
  standalone: true,
  imports: [ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, ModalDialogComponent, SingleselectboxComponent, RadiobuttonsComponent],
  templateUrl: './select-transport-dialog.component.html',
  styleUrl: './select-transport-dialog.component.scss'
})
export class SelectTransportDialogComponent {

  private dialog = inject(NgbActiveModal);
  public possibleTransportTypes = signal<TransportType[]>(null);
  public documentDirection = signal<DocumentDirection>(DocumentDirection.Inbound);
  public currentTransportType = signal<TransportType>(null);

  public selectedTransportTypeProtocol = signal<TransportProtocol>(null);
  public selectedEndpointOwner = signal<TransportEndpointOwner>(null);
  
  constructor() { 
    effect(() => {
      switch (this.currentTransportType()) {
        case TransportType.SFTPCustomerEndpoint:
          this.selectedTransportTypeProtocol.set(TransportProtocol.SFTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Customer);
          break;          
        case TransportType.SFTPPartnerEndpoint:
          this.selectedTransportTypeProtocol.set(TransportProtocol.SFTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Partner);
          break;
        case TransportType.IntegratedSFTP:
          this.selectedTransportTypeProtocol.set(TransportProtocol.SFTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Customer);
          break;
        case TransportType.FTPCustomerEndpoint:
          this.selectedTransportTypeProtocol.set(TransportProtocol.FTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Customer);
          break;
        case TransportType.FTPPartnerEndpoint:
          this.selectedTransportTypeProtocol.set(TransportProtocol.FTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Partner);
          break;
        case TransportType.IntegratedFTP:
          this.selectedTransportTypeProtocol.set(TransportProtocol.FTP);
          this.selectedEndpointOwner.set(TransportEndpointOwner.Customer);
          break;
        case TransportType.AS2:
          this.selectedTransportTypeProtocol.set(TransportProtocol.AS2);
          this.selectedEndpointOwner.set(
            this.documentDirection() === DocumentDirection.Inbound
              ? TransportEndpointOwner.Customer
              : TransportEndpointOwner.Partner
          );
          break;
        default:
          this.selectedTransportTypeProtocol.set(null);
          this.selectedEndpointOwner.set(null);
          break;
      }
    });
  }


  // Determine possible transport protocols based on possible transport types
  possibleTransportProtocols = computed(() => {
    const transportTypeProtocols = this.possibleTransportTypes().map(t => {
      switch (t) {
        case TransportType.SFTPCustomerEndpoint:
        case TransportType.SFTPPartnerEndpoint:
        case TransportType.IntegratedSFTP:
          return TransportProtocol.SFTP;
        case TransportType.FTPCustomerEndpoint:
        case TransportType.FTPPartnerEndpoint:
        case TransportType.IntegratedFTP:
          return TransportProtocol.FTP;
        case TransportType.AS2:
          return TransportProtocol.AS2;
        default:
          return null;
      }
    });
    return  [...new Set(transportTypeProtocols)]
  });

  // Determine possible endpoint owners based on selected protocol and possible transport types
  possibleEndpointOwners = computed(() => {
    switch (this.selectedTransportTypeProtocol()) {
      case TransportProtocol.SFTP:
      { 
        return [
          ...new Set(
            this.possibleTransportTypes()
              .filter(t =>
                t === TransportType.SFTPCustomerEndpoint ||
                t === TransportType.SFTPPartnerEndpoint ||
                t === TransportType.IntegratedSFTP
              )
              .map(t =>
                t === TransportType.SFTPPartnerEndpoint
                  ? TransportEndpointOwner.Partner
                  : TransportEndpointOwner.Customer
              )
          )
        ];
      }
      case TransportProtocol.FTP:
      { 
        return [
          ...new Set(
            this.possibleTransportTypes()
              .filter(t =>
                t === TransportType.FTPCustomerEndpoint ||
                t === TransportType.FTPPartnerEndpoint ||
                t === TransportType.IntegratedFTP
              )
              .map(t =>
                t === TransportType.FTPPartnerEndpoint
                  ? TransportEndpointOwner.Partner
                  : TransportEndpointOwner.Customer
              )
          )
        ];
      }
      case TransportProtocol.AS2:
        return this.documentDirection() === DocumentDirection.Inbound ?  [TransportEndpointOwner.Customer] : [TransportEndpointOwner.Partner];
      default:
        return [];
    }
  });

  // Provide descriptions for possible endpoint owners based on selected protocol and document direction
  possibleEndpointOwnersWithDescription = computed(() => this.possibleEndpointOwners().map(owner =>  {
      if (this.documentDirection() === DocumentDirection.Inbound) {
        switch (this.selectedTransportTypeProtocol()) {
          case TransportProtocol.SFTP:
            return owner === TransportEndpointOwner.Customer ? {value: owner, description: 'You will push documents to our SFTP server'} : {value: owner, description: 'We will pull documents from your SFTP server'};
          case TransportProtocol.FTP:
            return owner === TransportEndpointOwner.Customer ? {value: owner, description: 'You will push documents to our FTP server'} : {value: owner, description: 'We will pull documents from your FTP server'};
          case TransportProtocol.AS2:
            return {value: owner, description: 'You will send documents to our AS2 endpoint'};
          default:
            return '';
        }
      }
      else {
        switch (this.selectedTransportTypeProtocol()) {
          case TransportProtocol.SFTP:
            return owner === TransportEndpointOwner.Customer ? {value: owner, description: 'You will pull documents from our SFTP server'} : {value: owner, description: 'We will push documents to your SFTP server'};
          case TransportProtocol.FTP:
            return owner === TransportEndpointOwner.Customer ? {value: owner, description: 'You will pull documents from our FTP server'} : {value: owner, description: 'We will push documents to your FTP server'};
          case TransportProtocol.AS2:
            return {value: owner, description: 'We will send documents to your AS2 endpoint'};
          default:
            return '';
        }
      }
    }));  

  // Determine selected transport type based on chosen protocol and endpoint owner
  selectedTransportType = computed(() => {
    const protocol = this.selectedTransportTypeProtocol();
    const owner = this.selectedEndpointOwner();
    if (!protocol || !owner) {
      return null;
    }
    switch (protocol) {
      case TransportProtocol.SFTP:
        if (owner === TransportEndpointOwner.Customer) {
          return this.possibleTransportTypes().includes(TransportType.IntegratedSFTP) ? TransportType.IntegratedSFTP : TransportType.SFTPCustomerEndpoint;
        }
        else {
          return TransportType.SFTPPartnerEndpoint;
        }
      case TransportProtocol.FTP:
        if (owner === TransportEndpointOwner.Customer) {
          return this.possibleTransportTypes().includes(TransportType.IntegratedFTP) ? TransportType.IntegratedFTP : TransportType.FTPCustomerEndpoint;
        }
        else {
          return TransportType.FTPPartnerEndpoint;
        }
      case TransportProtocol.AS2:
        return TransportType.AS2;
      default:
        return null;
    }
  });

  transportProtocolChanged(event: TransportProtocol) {
    this.selectedTransportTypeProtocol.set(event);
    if (this.possibleEndpointOwners().length > 0) {
      this.selectedEndpointOwner.set(this.possibleEndpointOwners()[0]);
    }
    else {
      this.selectedEndpointOwner.set(null);
    }
  }

  endpointOwnerChanged(event: TransportEndpointOwner) {
    this.selectedEndpointOwner.set(event);
  }

  select() {
    this.dialog.close(new SelectTransportDialogResult({SelectedTransportType: this.selectedTransportType()}));
  }

  cancel() {
    this.dialog.dismiss();
  }
}

export class SelectTransportDialogResult {
  SelectedTransportType: TransportType;

  constructor(source?: Partial<SelectTransportDialogResult>) {
    if (source) {
        Object.assign(this, source);
    }
  }
}

export enum TransportEndpointOwner {
  Customer = 'Customer',
  Partner = 'Partner',
  Combibined = 'Combined'
}

export enum TransportProtocol {
  SFTP = 'SFTP',
  FTP = 'FTP',
  AS2 = 'AS2'
}

